<div class="customer-survey-detail-container">
  
  <!-- Header -->
  <div class="page-header">
    <h2>
      <button class="btn btn-secondary btn-sm mr-3" (click)="goBack()">
        <i class="fas fa-arrow-left"></i> Back
      </button>
      Survey Details
    </h2>
    <div class="header-actions">
      <button class="btn btn-primary" (click)="exportToExcel()" [disabled]="!surveyDetails">
        <i class="fas fa-file-excel"></i> Export to Excel
      </button>
      <button class="btn btn-info ml-2" (click)="reloadSurvey()" [disabled]="loading">
        <i class="fas fa-sync-alt" [class.fa-spin]="loading"></i> Refresh
      </button>
    </div>
  </div>

  <!-- Alert Messages -->
  <div *ngIf="successMessage" class="alert alert-success alert-dismissible fade show" role="alert">
    <i class="fas fa-check-circle"></i> {{ successMessage }}
    <button type="button" class="close" (click)="successMessage = ''">
      <span>&times;</span>
    </button>
  </div>

  <div *ngIf="errorMessage" class="alert alert-danger alert-dismissible fade show" role="alert">
    <i class="fas fa-exclamation-circle"></i> {{ errorMessage }}
    <button type="button" class="close" (click)="errorMessage = ''">
      <span>&times;</span>
    </button>
  </div>

  <!-- Loading Spinner -->
  <div *ngIf="loading" class="loading-container">
    <div class="spinner-border text-primary" role="status">
      <span class="sr-only">Loading...</span>
    </div>
    <p class="mt-2">Loading survey details...</p>
  </div>

  <!-- Survey Details Content -->
  <div *ngIf="surveyDetails && !loading" class="survey-details-content">
    
    <!-- Survey Header Info -->
    <div class="card mb-4">
      <div class="card-header bg-light">
        <h5 class="mb-0">
          {{ surveyDetails.survey_title }}
          <span class="badge ml-2" [ngClass]="getStatusClass(surveyDetails.status)">
            {{ surveyDetails.status }}
          </span>
        </h5>
      </div>
      <div class="card-body">
        <div class="row">
          <div class="col-md-6">
            <div class="info-item">
              <label>Survey Number:</label>
              <span class="font-weight-bold">{{ surveyDetails.survey_number }}</span>
            </div>
            <div class="info-item">
              <label>Description:</label>
              <span>{{ surveyDetails.survey_description || 'No description' }}</span>
            </div>
            <div class="info-item">
              <label>Created By:</label>
              <span>{{ surveyDetails.created_by?.employee_name || 'N/A' }}</span>
            </div>
          </div>
          <div class="col-md-6">
            <div class="info-item">
              <label>Created Date:</label>
              <span>{{ formatDate(surveyDetails.created_date_time) }}</span>
            </div>
            <div class="info-item">
              <label>Last Updated:</label>
              <span>{{ formatDate(surveyDetails.updated_at) }}</span>
            </div>
            <div class="info-item">
              <label>Completion:</label>
              <span>
                {{ surveyDetails.total_completed }} of {{ surveyDetails.total_assigned }} 
                ({{ getCompletionPercentage() }}%)
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Navigation Tabs -->
    <div class="card mb-4">
      <div class="card-header">
        <ul class="nav nav-tabs card-header-tabs">
          <li class="nav-item">
            <a class="nav-link" [class.active]="activeTab === 'overview'" 
               (click)="setActiveTab('overview')">
              <i class="fas fa-chart-pie mr-1"></i> Overview
            </a>
          </li>
          <li class="nav-item">
            <a class="nav-link" [class.active]="activeTab === 'questions'" 
               (click)="setActiveTab('questions')">
              <i class="fas fa-question-circle mr-1"></i> Questions ({{ safeArrayLength(surveyDetails.questions) }})
            </a>
          </li>
          <li class="nav-item">
            <a class="nav-link" [class.active]="activeTab === 'customers'" 
               (click)="setActiveTab('customers')">
              <i class="fas fa-users mr-1"></i> Assigned Customers ({{ safeArrayLength(surveyDetails.assigned_customers) }})
            </a>
          </li>
          <li class="nav-item">
            <a class="nav-link" [class.active]="activeTab === 'responses'" 
               (click)="setActiveTab('responses')">
              <i class="fas fa-clipboard-check mr-1"></i> Responses ({{ safeArrayLength(surveyDetails.responses) }})
            </a>
          </li>
        </ul>
      </div>

      <!-- Tab Content -->
      <div class="card-body">
        
        <!-- OVERVIEW TAB -->
        <div *ngIf="activeTab === 'overview'" class="overview-tab">
          <div class="row">
            
            <!-- Summary Cards -->
            <div class="col-md-3 mb-4">
              <div class="summary-card total">
                <div class="summary-icon">
                  <i class="fas fa-users"></i>
                </div>
                <div class="summary-content">
                  <h3>{{ surveyDetails.total_assigned || 0 }}</h3>
                  <p>Total Assigned</p>
                </div>
              </div>
            </div>

            <div class="col-md-3 mb-4">
              <div class="summary-card completed">
                <div class="summary-icon">
                  <i class="fas fa-check-circle"></i>
                </div>
                <div class="summary-content">
                  <h3>{{ surveyDetails.total_completed || 0 }}</h3>
                  <p>Completed</p>
                </div>
              </div>
            </div>

            <div class="col-md-3 mb-4">
              <div class="summary-card pending">
                <div class="summary-icon">
                  <i class="fas fa-clock"></i>
                </div>
                <div class="summary-content">
                  <h3>{{ surveyDetails.pending_count || ((surveyDetails.total_assigned || 0) - (surveyDetails.total_completed || 0)) }}</h3>
                  <p>Pending</p>
                </div>
              </div>
            </div>

            <div class="col-md-3 mb-4">
              <div class="summary-card percentage">
                <div class="summary-icon">
                  <i class="fas fa-percentage"></i>
                </div>
                <div class="summary-content">
                  <h3>{{ getCompletionPercentage() }}%</h3>
                  <p>Completion Rate</p>
                </div>
              </div>
            </div>
          </div>

          <!-- Progress Bar -->
          <div class="progress-section mt-4">
            <div class="d-flex justify-content-between mb-2">
              <span>Survey Progress</span>
              <span>{{ getCompletionPercentage() }}%</span>
            </div>
            <div class="progress" style="height: 20px;">
              <div class="progress-bar bg-success" 
                   role="progressbar" 
                   [style.width.%]="getCompletionPercentage()"
                   [attr.aria-valuenow]="getCompletionPercentage()" 
                   aria-valuemin="0" 
                   aria-valuemax="100">
                {{ getCompletionPercentage() }}%
              </div>
            </div>
          </div>

          <!-- Assigned Employees -->
          <div class="assigned-employees mt-4">
            <h6><i class="fas fa-user-tie mr-2"></i>Assigned Employees</h6>
            <div class="employee-tags">
              <span *ngFor="let employee of getAssignedEmployees()" class="badge badge-info mr-2 mb-2">
                {{ employee }}
              </span>
              <span *ngIf="getAssignedEmployees().length === 0" class="text-muted">
                No employees assigned
              </span>
            </div>
          </div>
        </div>

        <!-- QUESTIONS TAB -->
        <div *ngIf="activeTab === 'questions'" class="questions-tab">
          <div *ngIf="!surveyDetails.questions || surveyDetails.questions.length === 0" class="text-center text-muted py-5">
            <i class="fas fa-question-circle fa-3x mb-3"></i>
            <h5>No questions found</h5>
            <p>This survey doesn't have any questions yet</p>
          </div>

          <div *ngIf="surveyDetails.questions && surveyDetails.questions.length > 0" class="questions-list">
            <div *ngFor="let question of surveyDetails.questions; let i = index" class="question-item mb-4 p-3 border rounded">
              <div class="question-header d-flex justify-content-between align-items-center mb-2">
                <h6 class="mb-0">
                  <span class="badge badge-primary mr-2">Q{{ i + 1 }}</span>
                  {{ question.question_text }}
                </h6>
                <div>
                  <span class="badge" [ngClass]="question.is_required ? 'badge-danger' : 'badge-secondary'">
                    {{ question.is_required ? 'Required' : 'Optional' }}
                  </span>
                  <span class="badge badge-info ml-2">
                    <i [class]="getQuestionTypeIcon(question.question_type)" class="mr-1"></i>
                    {{ formatQuestionType(question.question_type) }}
                  </span>
                </div>
              </div>
              
              <div *ngIf="question.question_type === 'multiple_choice' && question.options && question.options.length > 0" 
                   class="question-options mt-2">
                <strong>Options:</strong>
                <ul class="mb-0 pl-3">
                  <li *ngFor="let option of question.options">{{ option }}</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        <!-- CUSTOMERS TAB -->
        <div *ngIf="activeTab === 'customers'" class="customers-tab">
          <div *ngIf="!surveyDetails.assigned_customers || surveyDetails.assigned_customers.length === 0" class="text-center text-muted py-5">
            <i class="fas fa-users fa-3x mb-3"></i>
            <h5>No customers assigned</h5>
            <p>No customers have been assigned to this survey yet</p>
          </div>

          <div *ngIf="surveyDetails.assigned_customers && surveyDetails.assigned_customers.length > 0" class="table-responsive">
            <table class="table table-hover">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Customer ID</th>
                  <th>Customer Name</th>
                  <th>Assigned To</th>
                  <th>Assigned Date</th>
                  <th>Status</th>
                  <th>Response</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr *ngFor="let customer of surveyDetails.assigned_customers; let i = index">
                  <td>{{ i + 1 }}</td>
                  <td>{{ customer.customer_id }}</td>
                  <td>{{ customer.customer_name }}</td>
                  <td>{{ getEmployeeDisplayName(customer) }}</td>
                  <td>{{ formatDateShort(customer.assigned_date) }}</td>
                  <td>
                    <span class="badge" [ngClass]="getCustomerStatusClass(customer.survey_status)">
                      {{ customer.survey_status }}
                    </span>
                  </td>
                  <td>
                    <span *ngIf="hasCustomerResponded(customer.customer_id)" class="badge badge-success">
                      <i class="fas fa-check"></i> Responded
                    </span>
                    <span *ngIf="!hasCustomerResponded(customer.customer_id)" class="badge badge-warning">
                      <i class="fas fa-clock"></i> Pending
                    </span>
                  </td>
                  <td>
                    <button *ngIf="canSubmitResponse() && !hasCustomerResponded(customer.customer_id)" 
                            class="btn btn-sm btn-success"
                            (click)="submitResponseForCustomer(customer)"
                            title="Submit Response">
                      <i class="fas fa-edit"></i> Submit
                    </button>
                    <button *ngIf="hasCustomerResponded(customer.customer_id)" 
                            class="btn btn-sm btn-info"
                            (click)="viewResponse(customer)"
                            title="View Response">
                      <i class="fas fa-eye"></i> View
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- RESPONSES TAB -->
        <div *ngIf="activeTab === 'responses'" class="responses-tab">
          <div *ngIf="!surveyDetails.responses || surveyDetails.responses.length === 0" class="text-center text-muted py-5">
            <i class="fas fa-clipboard-list fa-3x mb-3"></i>
            <h5>No responses yet</h5>
            <p>Responses will appear here once submitted</p>
          </div>

          <div *ngIf="surveyDetails.responses && surveyDetails.responses.length > 0" class="responses-list">
            <div *ngFor="let response of surveyDetails.responses" class="response-card mb-4 border rounded">
              <div class="response-header bg-light p-3">
                <div class="d-flex justify-content-between align-items-center">
                  <div>
                    <h6 class="mb-0">{{ response.customer_name }}</h6>
                    <small class="text-muted">Customer ID: {{ response.customer_id }}</small>
                  </div>
                  <div class="text-right">
                    <div class="text-muted small">
                      <i class="fas fa-calendar-alt mr-1"></i>
                      {{ formatDate(response.conducted_date) }}
                    </div>
                    <div class="text-muted small">
                      <i class="fas fa-user mr-1"></i>
                      Conducted by: {{ response.conducted_by_employee_name || response.conducted_by }}
                    </div>
                  </div>
                </div>
              </div>
              
              <div class="response-body p-3">
                <!-- Answers -->
                <div class="answers-section">
                  <h6 class="border-bottom pb-2 mb-3">Answers</h6>
                  <div *ngFor="let answer of response.answers" class="answer-item mb-3">
                    <div class="question-text mb-2">
                      <strong>{{ getQuestionText(answer.question_id) }}</strong>
                    </div>
                    <div class="answer-value ml-3">
                      <!-- Star Rating -->
                      <div *ngIf="answer.star_rating" class="star-rating-display">
                        <div class="stars d-inline-block">
                          <i *ngFor="let star of [1,2,3,4,5]" 
                             class="fas fa-star"
                             [class.text-warning]="star <= answer.star_rating"
                             [class.text-muted]="star > answer.star_rating"></i>
                        </div>
                        <span class="ml-2">({{ answer.star_rating }}/5)</span>
                      </div>
                      
                      <!-- Selected Option -->
                      <div *ngIf="answer.selected_option" class="selected-option">
                        <span class="badge badge-info badge-pill">{{ answer.selected_option }}</span>
                      </div>
                      
                      <!-- Text Answer -->
                      <div *ngIf="answer.text_answer" class="text-answer">
                        <div class="card bg-light">
                          <div class="card-body p-2">
                            {{ answer.text_answer }}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                
                <!-- Remarks -->
                <div *ngIf="response.remarks" class="remarks-section mt-3">
                  <h6 class="border-bottom pb-2 mb-2">
                    <i class="fas fa-comment mr-1"></i> Remarks
                  </h6>
                  <div class="card bg-light">
                    <div class="card-body p-3">
                      {{ response.remarks }}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
      </div>
    </div>
  </div>

</div>